import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  Alert,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Token as TokenIcon,
  Savings as SavingsIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { TOKEN_PACKAGES, TokenPackage, TokenBalance, TokenUsage, PaymentMethod } from '../../types/billing';
import { billingApi } from '../../services/api/billing';

interface TokenPurchaseProps {
  organizationId: string;
  tokenBalance: TokenBalance;
  tokenUsage: TokenUsage;
  paymentMethods: PaymentMethod[];
  onPurchaseComplete: () => void;
}

export const TokenPurchase: React.FC<TokenPurchaseProps> = ({
  organizationId,
  tokenBalance,
  tokenUsage,
  paymentMethods,
  onPurchaseComplete,
}) => {
  const [selectedPackage, setSelectedPackage] = useState<TokenPackage | null>(null);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>('');
  const [purchasing, setPurchasing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  const calculateMonthlyRunRate = () => {
    const daysInMonth = 30;
    const currentDay = new Date().getDate();
    const dailyRate = tokenUsage.totalTokens / currentDay;
    return Math.round(dailyRate * daysInMonth);
  };

  const handleSelectPackage = (pkg: TokenPackage) => {
    setSelectedPackage(pkg);
    setPaymentDialogOpen(true);
    setError(null);
    setSuccess(false);
  };

  const handlePurchase = async () => {
    if (!selectedPackage || !selectedPaymentMethod) return;

    setPurchasing(true);
    setError(null);

    try {
      await billingApi.purchaseTokens(
        organizationId,
        selectedPackage.id,
        1, // quantity
        selectedPaymentMethod
      );

      setSuccess(true);
      setTimeout(() => {
        setPaymentDialogOpen(false);
        onPurchaseComplete();
      }, 2000);
    } catch (err) {
      console.error('Error purchasing tokens:', err);
      setError('Failed to complete purchase. Please try again.');
    } finally {
      setPurchasing(false);
    }
  };

  const monthlyRunRate = calculateMonthlyRunRate();
  const recommendedPackage = TOKEN_PACKAGES.find(pkg => pkg.tokens >= monthlyRunRate * 3) || TOKEN_PACKAGES[TOKEN_PACKAGES.length - 1];

  return (
    <div>
      {/* Current Balance and Usage */}
      <Grid container spacing={3} style={{ marginBottom: 32 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
                <TokenIcon style={{ marginRight: 8, color: '#1976d2' }} />
                <Typography variant="h6">Current Balance</Typography>
              </div>
              <Typography variant="h4" fontWeight="bold">
                {formatNumber(tokenBalance.availableTokens)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Available tokens
              </Typography>
              {tokenBalance.prepurchasedTokens > 0 && (
                <div style={{ marginTop: 16 }}>
                  <LinearProgress
                    variant="determinate"
                    value={(tokenBalance.usedTokens / tokenBalance.prepurchasedTokens) * 100}
                    style={{ marginBottom: 8 }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {formatNumber(tokenBalance.usedTokens)} / {formatNumber(tokenBalance.prepurchasedTokens)} used
                  </Typography>
                </div>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
                <TrendingUpIcon style={{ marginRight: 8, color: '#ed6c02' }} />
                <Typography variant="h6">Monthly Usage</Typography>
              </div>
              <Typography variant="h4" fontWeight="bold">
                {formatNumber(tokenUsage.totalTokens)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Tokens this month
              </Typography>
              <Typography variant="body2" style={{ marginTop: 8 }}>
                Run rate: ~{formatNumber(monthlyRunRate)}/month
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
                <SavingsIcon style={{ marginRight: 8, color: '#2e7d32' }} />
                <Typography variant="h6">Savings</Typography>
              </div>
              <Typography variant="h4" fontWeight="bold">
                ${tokenUsage.prepurchasedTokensUsed > 0 ? (tokenUsage.prepurchasedTokensUsed * 0.002).toFixed(2) : '0.00'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Saved with prepurchased tokens
              </Typography>
              <Typography variant="body2" style={{ marginTop: 8 }}>
                10% discount on prepurchased
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Purchase Options */}
      <Typography variant="h6" gutterBottom>
        Purchase Token Packages
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Save 10% on all token packages. Tokens never expire and are used automatically before pay-as-you-go billing.
      </Typography>

      <Grid container spacing={3}>
        {TOKEN_PACKAGES.map((pkg) => (
          <Grid item xs={12} md={4} key={pkg.id}>
            <Card
              style={{
                height: '100%',
                position: 'relative',
                border: pkg.id === recommendedPackage.id ? '2px solid #1976d2' : '1px solid rgba(0,0,0,0.12)',
              }}
            >
              {pkg.popular && (
                <Chip
                  label="MOST POPULAR"
                  size="small"
                  color="primary"
                  style={{
                    position: 'absolute',
                    top: 10,
                    right: 10,
                  }}
                />
              )}
              {pkg.id === recommendedPackage.id && !pkg.popular && (
                <Chip
                  label="RECOMMENDED"
                  size="small"
                  color="secondary"
                  style={{
                    position: 'absolute',
                    top: 10,
                    right: 10,
                  }}
                />
              )}
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {pkg.name}
                </Typography>
                <Typography variant="h3" fontWeight="bold">
                  ${pkg.discountedPrice}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <s>${pkg.basePrice}</s> Save ${pkg.savings}
                </Typography>
                <div style={{ marginTop: 16, marginBottom: 16 }}>
                  <Typography variant="body2">
                    {formatNumber(pkg.tokens)} tokens
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    ${(pkg.discountedPrice / pkg.tokens * 1000).toFixed(2)} per 1K tokens
                  </Typography>
                </div>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => handleSelectPackage(pkg)}
                >
                  Purchase
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Purchase Dialog */}
      <Dialog open={paymentDialogOpen} onClose={() => setPaymentDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Complete Purchase</DialogTitle>
        <DialogContent>
          {success ? (
            <div style={{ textAlign: 'center', paddingTop: 32, paddingBottom: 32 }}>
              <CheckCircleIcon style={{ fontSize: 64, color: '#2e7d32', marginBottom: 16 }} />
              <Typography variant="h6" gutterBottom>
                Purchase Successful!
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Your tokens have been added to your account.
              </Typography>
            </div>
          ) : (
            <>
              {error && (
                <Alert severity="error" style={{ marginBottom: 16 }} onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}
              
              {selectedPackage && (
                <div style={{ marginBottom: 24 }}>
                  <Typography variant="h6" gutterBottom>
                    {selectedPackage.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatNumber(selectedPackage.tokens)} tokens for ${selectedPackage.discountedPrice}
                  </Typography>
                </div>
              )}

              <FormControl fullWidth style={{ marginBottom: 16 }}>
                <InputLabel>Payment Method</InputLabel>
                <Select
                  value={selectedPaymentMethod}
                  onChange={(e) => setSelectedPaymentMethod(e.target.value)}
                  disabled={purchasing}
                >
                  {paymentMethods.map((method) => (
                    <MenuItem key={method.id} value={method.id}>
                      {method.brand} •••• {method.lastFour}
                      {method.isDefault && ' (Default)'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Alert severity="info" style={{ marginTop: 16 }}>
                Tokens will be available immediately after purchase and will be used automatically before pay-as-you-go billing.
              </Alert>
            </>
          )}
        </DialogContent>
        <DialogActions>
          {!success && (
            <>
              <Button onClick={() => setPaymentDialogOpen(false)} disabled={purchasing}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handlePurchase}
                disabled={!selectedPaymentMethod || purchasing}
              >
                {purchasing ? 'Processing...' : 'Complete Purchase'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </div>
  );
};